/** The one row the Teams block draws, kept beside it rather than inside it.
 *
 *  Its own file because it is the half of that file with a different job: the block
 *  owns the flag gate, the read, the Microsoft round trip and two global actions,
 *  while this owns one question — what is true about this workspace, and how is that
 *  said in one badge and one line. It reads no store and holds no state; everything
 *  it renders arrives as a prop, which is what makes the seam a real one.
 */
import { observer } from "mobx-react";
import { useTranslation } from "@/i18n";
import { Badge } from "@/components/ui/badge";
import { TruncatedText } from "@/components/ui/truncated-text";
import { isConnected } from "@/lib/integrations/msteams-link";
import type { TTeamsWorkspaceLink, TTeamsLinkState } from "@/types/msteams-link";

/** How a row that is **not** receiving explains itself, in one line.
 *
 *  A key per link state rather than one generic "not connected", because the recourse
 *  differs and only the person can act: `unlinked` means they turned it off here (so
 *  Connect is the fix), `uninstalled` means they removed the app in Teams (so
 *  installing is), `blocked` means Teams itself is refusing, and `unmatched` means we
 *  can see an install we cannot attribute to them. A single line for all four would
 *  make three of them look like the fourth.
 *
 *  Typed as a total map over the state union rather than inferred, so a state added
 *  to the API fails the build here instead of rendering an empty line: `t(undefined)`
 *  is not a type error at the call site, and a missing reason looks exactly like a
 *  state that legitimately has nothing to say.
 */
const REASON_KEY_BY_STATE: Record<TTeamsLinkState, string> = {
  matched: "profile_settings.msteams.reason_not_installed",
  unmatched: "profile_settings.msteams.reason_unmatched",
  unlinked: "profile_settings.msteams.reason_unlinked",
  uninstalled: "profile_settings.msteams.reason_uninstalled",
  blocked: "profile_settings.msteams.reason_blocked",
};

/** The current workspace's row: its name, one badge, and one line saying what is true.
 *
 *  **Four badges, because there are four states and three of them are not the same
 *  thing.** `receiving` is the server's own answer — derived from the predicate the
 *  delivery gate uses (connection enabled AND link matched AND the app installed) —
 *  and it is the only thing that earns the success badge, so the badge can never
 *  claim a channel the emit path would skip.
 *
 *  The next two split on the **link**, not on `!receiving`, because the badge
 *  answers "is my side of this done" and `!receiving` is also false where the
 *  person has done everything they can. A matched link earns *Connected* whether or
 *  not the app is installed and whether or not the admin has paused the channel —
 *  calling either of those "Not connected" contradicted the row's own reason line,
 *  which said the opposite in the next breath. Only a link that is absent or not
 *  matched (unlinked, uninstalled, blocked, unmatched) is genuinely not connected,
 *  and that is exactly the set the Connect button offers itself to, so the badge and
 *  the button can never disagree.
 *
 *  The fourth is `row === null`, which is not a state of theirs at all: the payload
 *  lists only workspaces that **have** a Teams connection, so a missing row means the
 *  workspace never enabled the channel. *Not connected* would blame the person for an
 *  admin's decision and point them at a Connect button that would change nothing
 *  here, so it reads *Not set up* and says whose call it was. The block still draws
 *  its global actions beneath, because a link in some other workspace is still live.
 *
 *  The reason line carries the difference the badge deliberately drops: install the
 *  app, or wait for your admin.
 */
export const TeamsLinkRow = observer(function TeamsLinkRow({
  workspaceName,
  row,
}: {
  workspaceName: string;
  row: TTeamsWorkspaceLink | null;
}) {
  const { t } = useTranslation();

  // Checked before the link, because it outranks it: whatever the person's own state
  // is, a paused workspace sends nothing and there is nothing they can do about it.
  const paused = row?.connection_state === "disabled";
  // The badge answers "is MY side of this done", so it reads the link and nothing
  // else. `!receiving` is the wrong question: it is also false for a workspace whose
  // admin paused the channel, where the person has done everything they can.
  const linkMatched = row !== null && isConnected(row);
  const reason = !row
    ? t("profile_settings.msteams.workspace_not_set_up")
    : paused
      ? t("profile_settings.msteams.workspace_paused")
      : row.receiving
        ? null
        : row.link
          ? t(REASON_KEY_BY_STATE[row.link.state])
          : t("profile_settings.msteams.reason_none");

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="max-w-full min-w-0 text-sm font-medium text-foreground">
        <TruncatedText text={workspaceName} />
      </span>
      {row?.receiving ? (
        <Badge variant="status-success">{t("profile_settings.msteams.state_receiving")}</Badge>
      ) : linkMatched ? (
        <Badge variant="status-info">{t("profile_settings.msteams.state_connected")}</Badge>
      ) : row ? (
        <Badge variant="status-neutral">{t("profile_settings.msteams.state_not_connected")}</Badge>
      ) : (
        <Badge variant="status-neutral">{t("profile_settings.msteams.state_not_set_up")}</Badge>
      )}
      {reason ? <span className="basis-full text-xs text-muted-foreground">{reason}</span> : null}
    </div>
  );
});
